// Section 19: Discovery. 5% per season, Spring and Summer bias. Foragers find a herb patch
// (+1 medicine a season), a fish run (+1 food per working forager a season), both for 4 seasons,
// or a clay bed holding a spring (a new well site: the well limit rises by one). Choices: send 2
// foragers to develop it (they lose their output this season; the gain starts next season) or
// ignore it. Constants: config.events.discovery.
import type { Config } from '../../config';
import {
  choiceOutcome,
  effectDelta,
  eventEntry,
  eventPrompt,
  eventRng,
  promptString,
} from '../../core/content-helpers';
import { mod } from '../../core/modifiers';
import { isWorking } from '../../core/queries';
import { defineEvent, type EventChoiceDef } from '../../core/registry';
import type { Delta, Modifier, Person, PersonId, ReadContext } from '../../core/types';

const FINDS = ['herb-patch', 'fish-run', 'clay-bed'] as const;
type Find = (typeof FINDS)[number];

function foragers(ctx: ReadContext): Person[] {
  return (Object.keys(ctx.state.people).sort() as PersonId[])
    .map((id) => ctx.state.people[id])
    .filter((p): p is Person => p !== undefined && p.job === 'forager' && isWorking(p));
}

function findOf(s: string): Find | null {
  return (FINDS as readonly string[]).includes(s) ? (s as Find) : null;
}

export function discovery(config: Config) {
  const c = config.events.discovery;
  const find = (ctx: ReadContext): Find => eventRng(ctx, 'discovery', 'find').pick(FINDS);
  const choices: EventChoiceDef[] = [
    {
      id: 'develop',
      label: 'Send two foragers to develop it',
      preview: (ctx, prompt) => [
        {
          key: `discovery-${findOf(promptString(prompt, 'variant')) ?? 'herb-patch'}`,
          params: {
            foragers: Math.min(c.foragersSent, foragers(ctx).length),
            seasons: c.bonusSeasons,
          },
        },
      ],
      apply: (ctx, prompt) => {
        const variant = findOf(promptString(prompt, 'variant'));
        const sent = foragers(ctx).slice(0, c.foragersSent);
        if (variant === null || sent.length === 0)
          return [choiceOutcome(prompt, 'develop', { variant: variant ?? '', foragers: 0 })];
        const now = ctx.season.turn;
        const out: Delta[] = [
          effectDelta(
            'discovery',
            'sent',
            now,
            1,
            { workerOutputMul: 0 },
            sent.map((p) => p.id),
          ),
        ];
        if (variant === 'herb-patch')
          out.push(
            effectDelta('discovery', variant, now + 1, c.bonusSeasons, {
              medicinePerSeason: c.medicinePerSeason,
            }),
          );
        if (variant === 'fish-run')
          out.push(
            effectDelta('discovery', variant, now + 1, c.bonusSeasons, {
              foragerFood: c.foodPerForager,
            }),
          );
        if (variant === 'clay-bed')
          out.push(
            effectDelta('discovery', variant, now + 1, Number.MAX_SAFE_INTEGER - now - 1, {
              'limit:well': c.wellSites,
            }),
          );
        out.push(
          choiceOutcome(prompt, 'develop', {
            variant,
            foragers: sent.length,
            names: sent.map((p) => p.name).join(', '),
          }),
        );
        return out;
      },
    },
    {
      id: 'ignore',
      label: 'Ignore it',
      preview: null,
      apply: (_ctx, prompt) => [choiceOutcome(prompt, 'ignore')],
    },
  ];
  return defineEvent({
    id: 'discovery',
    label: 'Discovery',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring', 'summer'],
    foreshadow: null,
    ledgerTag: null,
    weight: () => c.baseChance,
    text: (ctx) => eventEntry('discovery', { variant: find(ctx) }),
    apply: (ctx) => [
      eventPrompt(ctx, 'discovery', choices, {
        params: { variant: find(ctx) },
        defaultChoiceId: 'ignore',
      }),
    ],
    choices,
    modifierTargets: ['output'],
    modifiers(_ctx, target, effect): Modifier[] {
      if (target.kind !== 'output') return [];
      const p = effect.params;
      if (p.workerOutputMul !== undefined && effect.subjectIds?.includes(target.personId))
        return [mod.mul('event:discovery', p.workerOutputMul, 'developing a find')];
      if (p.foragerFood !== undefined && target.jobId === 'forager')
        return [mod.add('event:discovery', p.foragerFood, 'fish run')];
      return [];
    },
  });
}
